import { Component, OnDestroy, OnInit, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { DatePipe, formatDate, formatNumber } from '@angular/common';
import { concat, of, from, iif, filter, Subscription, Subject, tap } from 'rxjs';
import { map, switchMap, concatMap, finalize, debounceTime, takeUntil } from 'rxjs/operators';

import { Table } from 'primeng/table';

import { environment } from 'src/environments/environment';
import { round1, round2, stripVat } from 'src/app/shared/round';

import { PriceVariable } from 'src/app/shared/price-variable';
import { Product, ProdVendors } from 'src/app/shared/product'
import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { ProductApiService } from 'src/app/services/product-api.service';
import { ClientApiService } from 'src/app/services/client-api.service';
import { RestApiService } from 'src/app/services/rest-api.service';
import { Currency } from '../../shared/currency';

@Component({
  selector: 'app-edit-product-vendors',
  templateUrl: './edit-product-vendors.component.html',
  styleUrls: ['./edit-product-vendors.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EditProductVendorsComponent implements OnInit, OnDestroy {

  globalSettinsData: GlobalSettingsData;

  @Input() product: Product;

  @Output() productVendor: EventEmitter<ProdVendors> = new EventEmitter();
  @Output() productPriceUpdate: EventEmitter<ProdVendors[]> = new EventEmitter();

  prodVendors: ProdVendors[];
  selectedProdVendor: ProdVendors;

  prodVendorsClone: ProdVendors[] = [];

  editMode: boolean = false;

  /** `true` while `reloadProdVendors` is waiting for the server; the parent's Reload button reads it. */
  reloading: boolean = false;

  mainCurrencyColumnName: string = "В основной валюте";

  roundVatPriceCurr: number = 0.01;
  roundVatPriceMain: number = 0.01;

  public get mainPvRow(): boolean {
    return (this.selectedProdVendor && this.product && (this.selectedProdVendor.VENDORSITEMID == this.product.PRODPRICEVID));
  }

  public get disableSaveRow(): boolean {
    return (
      !this.selectedProdVendor
        || (this.selectedProdVendor.VENDORID == null)
        || (this.selectedProdVendor.PRODID == null)
        || (this.selectedProdVendor.VPCURRID == null));
  }

  public get rowDetails(): number {
    return (
      this.selectedProdVendor ? this.selectedProdVendor.VENDORID : -1);
  }

  public get canNotSetAsMainRow(): boolean {

    if (!this.selectedProdVendor) return true;
    if (!this.product) return true;

    true || environment.production || console.log('VENDORSITEMID: ' + this.selectedProdVendor.VENDORSITEMID + ', PRODPRICEVID: ' + this.product.PRODPRICEVID);
    if (this.selectedProdVendor.VENDORSITEMID == this.product.PRODPRICEVID) return true;

    return false;
  }

  @ViewChild('PVT') prodVendorsTable: Table;

  rowIndex: number;

  private _productQuery: Subscription;
  private _productVendorsQuery: Subscription;
  private _prodPutQuery: Subscription;
  private _pvProdQuery: Subscription;

  private readonly destroy$ = new Subject<void>();
  /** Fires to abandon an in-flight `reloadProdVendors` (an edit is starting). */
  private readonly _reloadCancel$ = new Subject<void>();

  private cliInfoQuery: Subscription;

  private pvPriceUpdate$ = new Subject<ProdVendors>();

  constructor(
    private cliApi: ClientApiService,
    private prodApi: ProductApiService,
    private restApi: RestApiService,
    private globalsettingsservice: GetglobalsettingsService,
  ) { }

  ngOnDestroy(): void {
    console.log('*** EditProductVendorsComponent in ngOnDestroy')

    if (this._productQuery) { this._productQuery.unsubscribe(); }

    if (this._productVendorsQuery) { this._productVendorsQuery.unsubscribe(); }

    if (this._prodPutQuery) { this._prodPutQuery.unsubscribe(); }

    if (this.cliInfoQuery) { this.cliInfoQuery.unsubscribe(); }

    if (this._pvProdQuery) { this._pvProdQuery.unsubscribe(); }

    this.destroy$.next();
    this.destroy$.complete();

  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('*** EditProductVendorsComponent ngOnChanges:');
    for (let propName in changes) {
      let change = changes[propName];

      //environment.production || console.log('*** EditProductVendorsComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //environment.production || console.log('*** EditProductVendorsComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'product':
          this.loadProdVendors();
          break;
      }
    }
  }

  /**
   * Re-reads the vendor rows (and their calculated prices) from the database without reloading the
   * page, then tells the parent so the other pricing sections refresh too (`productPriceUpdate`).
   *
   * Does nothing while a row is being edited or a reload is already running: replacing the rows
   * would silently throw away the unsaved row (and an added, not yet saved one). An edit started
   * while a reload is still in flight cancels that reload (`onPvRowEditInit`), for the same reason.
   * A failed request leaves the rows on screen as they were; `ProductApiService.handleError`
   * already told the user.
   */
  reloadProdVendors() {
    if (this.reloading || this.editMode || !this.product) { return; }

    this.reloading = true;

    this.prodVendors$()
      .pipe(
        takeUntil(this._reloadCancel$),
        takeUntil(this.destroy$),
        finalize(() => { this.reloading = false; }))
      .subscribe({
        next: () => this.productPriceUpdate.emit(this.prodVendors),
        error: err => console.error('*** EditProductVendorsComponent reloadProdVendors failed: ', err)
      });
  }

  loadProdVendors() {
    this.prodVendors$().subscribe();
  }

  /** Fetches the vendor rows, decorates them with the price-change figures, and publishes them to the table. */
  private prodVendors$() {
    return this.prodApi.getProdVendors(this.product.PRODID)
      .pipe(map(data => {
        console.log('*** EditProductVendorsComponent loadProdVendors: ===> map');
        environment.production || console.table(data);
        this.prodVendors = data;
        this.prodVendors.forEach(e => {
          e.cloneIndex = 0;
          e.vpc = ((e.VENDORPRICE / e.VENDORPREVPRICE) - 1) * 100 ;
          e.ppc = ((e.VPOUTCOMEPRICE / e.PRODPREVPRICE) - 1) * 100;

          e.vpcstr = '';

          if (e.vpc > 0.5) {
            e.vpcstr = 'Цена выросла на ' + formatNumber( e.vpc, 'uk', '1.0-0') + '%';
          } else
          if (e.vpc < -0.5) {
            e.vpcstr = 'Цена снизилась на ' + formatNumber(-e.vpc, 'uk', '1.0-0') + '%';
          }
          e.outComePriceInMainCurrency = e.VPOUTCOMEPRICE * e.CURRATE;
        });
        environment.production || console.table(this.prodVendors);
        this.product._PRODVENDORS = this.prodVendors;
      }));
  }

  calculateIncomePrice(pv: ProdVendors) {

  }

  calculateOutcomePrice(pv: ProdVendors) {

  }

  ngOnInit(): void {
    console.log('*** EditProductVendorsComponent in ngOnInit')

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

      this._pvProdQuery = this.pvPriceUpdate$.pipe(

        debounceTime(250),
        filter(pv => ((pv != null) && (pv.VPCURRID != null))),
        
        tap(pv => (environment.production || console.table(pv))),

        switchMap(pv => this.restApi.getCurrencyInfoByID(pv.VPCURRID).pipe(

          filter(cv => (cv && (cv.length > 0))),
          concatMap(cv => cv),
          tap(cv => (environment.production || console.table(cv))),
          concatMap(cv => {

            pv.CURCODE = cv.CURCODE;
            pv.CURNAME = cv.CURNAME;
            pv.CURRATE = cv.CURRATE;

            pv.VAVARVALUE = 0;
            pv.VMVARVALUE = 1;

            return of(pv);
          }),

          concatMap(pv => this.prodApi.getPriceVariableById(pv.VPADDVAR || 0).pipe(
            tap(v => (environment.production || console.table(v))),

            filter(v => (v.length > 0)),
            concatMap(v => v),
            concatMap(v => {

              pv.VAVARCODE = v.VARCODE;
              pv.VAVARNOTE = v.VARNOTE;
              pv.VAVARVALUE = parseFloat(v.VARVALUE);

              return of(pv);
            })
          )),

          concatMap(pv => this.prodApi.getPriceVariableById(pv.VPMULVAR || 1).pipe(
            tap(v => (environment.production || console.table(v))),

            filter(v => (v.length > 0)),
            concatMap(v => v),
            concatMap(v => {

              pv.VMVARCODE = v.VARCODE;
              pv.VMVARNOTE = v.VARNOTE;
              pv.VMVARVALUE = parseFloat(v.VARVALUE);

              return of(pv);
            })
          )),

          tap(pv => (environment.production || console.table(pv))),

          concatMap(pv => {

            if (this.product && (this.product.PRODKIND == 0)) {

              pv.VPINCOMEPRICE = (pv.VENDORPRICE + (pv.VPADDCONST || 0) + (pv.VAVARVALUE || 0)) * (pv.VPMULCONST || 1) * (pv.VMVARVALUE || 1);
              pv.VPOUTCOMEPRICE = pv.VENDORMARGIN * pv.VPINCOMEPRICE;

              pv.outComePriceInMainCurrency = pv.VPOUTCOMEPRICE * pv.CURRATE;

              this.roundVatPriceCurr = round2(Math.round(this.stripProductVat(pv.VPOUTCOMEPRICE)) * ((100.0 + this.product.PRODVAT) / 100.0));
              this.roundVatPriceMain = round2(Math.round(this.stripProductVat(pv.outComePriceInMainCurrency)) * ((100.0 + this.product.PRODVAT) / 100.0));

            } else {

            }

            return of(pv);
          }))))
        .subscribe();

    this.loadProdVendors();
  }

  onPvRowSelect(event) {
    console.log('*** EditProductVendorsComponent onPvRowSelect: ' + JSON.stringify(event));
  }

  onPvRowUnselect(event) {
    console.log('*** EditProductVendorsComponent onPvRowUnselect: ' + JSON.stringify(event));
  }

  onAddVendorInfo(event) {
    console.log('*** EditProductVendorsComponent onAddVendorInfo: ' + JSON.stringify(event));
    let newPV: ProdVendors = {

      VENDORSITEMID: -2,
      VENDORID: null,
      PRODID: this.product.PRODID,
      VENDORNOTE: 'New vendor note',
      VENDORPRICE: 0,
      VPCURRID: this.globalSettinsData.balanceCurrencyId,
      VPINCOMPRICEXPR: '',
      EUPRICEEXPR: '',
      VENDORMARGIN: 1.55,
      VENROUNDBASE: 1,
      VPADDVAR: null,
      VPADDCONST: 0,
      VPMULVAR: null,
      VPMULCONST: 1,
      cloneIndex: 0

    };

    this.prodVendors = [...this.prodVendors, newPV];
    this.onPvRowEditInit(this.prodVendors.find(e => e.VENDORSITEMID == -2));
    this.prodVendorsTable.initRowEdit(this.prodVendors.find(e => e.VENDORSITEMID == -2));
  }

  onRemoveVendorInfo(event) {
    console.log('*** EditProductVendorsComponent onRemoveVendorInfo: ' + JSON.stringify(event));

    let vr = this.prodVendors.findIndex(e => e.VENDORSITEMID == this.selectedProdVendor.VENDORSITEMID)

    if (vr != -1) {
      this._productVendorsQuery =
        this.prodApi.deleteProdVendorByRowId(this.selectedProdVendor.PRODID, this.selectedProdVendor.VENDORSITEMID)
        .subscribe(data => {
          console.log('*** EditProductVendorsComponent onRemoveVendorInfo affected Rows: ' + JSON.stringify(data));

          if (data) { }

          this.prodVendors.splice(vr, 1);
          this.prodVendors = [...this.prodVendors];

//  We don't need formally because active vendor's row couldn't be deleted
//
//          this.productPriceUpdate.emit(this.prodVendors);
      })
    }
  }

  onChangeVendorInfo(event) {
    console.log('*** EditProductVendorsComponent onChangeVendorInfo: ' + JSON.stringify(event));

    if (this.selectedProdVendor) {
      this.productVendor.emit(this.selectedProdVendor);
    }
  }

  onCurrencySelect(event: Currency, prodVendor: ProdVendors) {
    console.log('*** EditProductVendorsComponent onCurrencySelect: ' + JSON.stringify(event));

    if (event && 'CURID' in event) {
      prodVendor.VPCURRID = event.CURID;
      this.pvPriceUpdate$.next(prodVendor);
    }
  }

  onVendorIdSelect(event: number, prodVendor: ProdVendors) {
    console.log('*** EditProductVendorsComponent onVendorIdSelect: ' + JSON.stringify(event));

    prodVendor.VENDORID = event;

    this.cliInfoQuery =
      this.cliApi.getClientById(prodVendor.VENDORID)
        .pipe(
          switchMap(cli => from(cli)),

          concatMap(cli => {
            prodVendor.VPCURRID = cli.CURID ?? this.globalSettinsData.balanceCurrencyId;
            return of(cli);
          })
        )
        .subscribe();
  }

  onVaVarSelection(event: PriceVariable, prodVendor: ProdVendors) {
    console.log('*** EditProductVendorsComponent onVaVarSelection: ' + JSON.stringify(event));

    prodVendor.VPADDVAR = (event) ? event.VARID : null;
    this.pvPriceUpdate$.next(prodVendor);
  }

  onVmVarSelection(event: PriceVariable, prodVendor: ProdVendors) {
    console.log('*** EditProductVendorsComponent onVmVarSelection: ' + JSON.stringify(event));

    prodVendor.VPMULVAR = (event) ? event.VARID : null;
    this.pvPriceUpdate$.next(prodVendor);
  }

  /**
   * Strips VAT from a gross price using the product's real VAT rate. The product VAT may be
   * 18, 20 or 21 percent, so the divisor must come from `PRODVAT` (B-43; the old `/ 6 * 5`
   * assumed 20% only). A missing `PRODVAT` is not masked: the result is NaN, as before.
   */
  private stripProductVat(gross: number): number {
    return stripVat(gross, this.product.PRODVAT);
  }

  onPvRowEditInit(esg: ProdVendors) {
    console.log('*** EditProductVendorsComponent in onPvRowEditInit: entry');
    environment.production || console.table(esg);

    // Covers editing an existing row and adding a vendor (onAddVendorInfo ends up here): a reload
    // that answers later would replace this.prodVendors and wipe the row the user is about to edit.
    if (this.reloading) { this._reloadCancel$.next(); }

    this.roundVatPriceCurr = round2(Math.round(this.stripProductVat(esg.VPOUTCOMEPRICE)) * ((100.0 + this.product.PRODVAT) / 100.0));
    this.roundVatPriceMain = round2(Math.round(this.stripProductVat(esg.outComePriceInMainCurrency)) * ((100.0 + this.product.PRODVAT) / 100.0));

    this.editMode = true;

    let newCloneIndex: number = 0;

    /*
     * Quite tricky algoritm:
     * Because we can edit simultaneously more than one row in the table.
     * Therefore for every edited row we have to create cloned copy in shadow array [securityGroupsClone]
     * We can't predict the order of starting and ending the edit process
     * 
     * So - Search for max cloneIndex in current table and assign +1 to newly created row copy
     *
     * When row either cancelled or saved - clone index resetted to 0, clone copy deleted from shadow array
     *
     */
    this.prodVendors.forEach(pv => { newCloneIndex = Math.max(newCloneIndex, pv.cloneIndex) });

    environment.production || console.log('*** EditProductVendorsComponent in onRowEditInit: newCloneIndex is: ' + newCloneIndex);

    esg.cloneIndex = ++newCloneIndex;
    this.prodVendorsClone.push({ ...esg });

    this.selectedProdVendor = esg;
  }

  insertPriceInMain() {
    console.log('*** EditProductVendorsComponent insertPriceInMain: ');
    if (this.editMode && this.selectedProdVendor) {
      this.selectedProdVendor.VPOUTCOMEPRICE = this.roundVatPriceMain / this.selectedProdVendor.CURRATE;
      this.selectedProdVendor.VENDORMARGIN = this.selectedProdVendor.VPOUTCOMEPRICE / this.selectedProdVendor.VPINCOMEPRICE;
      this.pvPriceUpdate$.next(this.selectedProdVendor);
    }
  }

  onPvRowEditCancel(esg: ProdVendors, index: number) {
    console.log('*** EditProductVendorsComponent in onRowEditCancel: ' + index);
    environment.production || console.table(esg);

    if (esg.VENDORSITEMID == -2) {
      // Cancel the newly created record
      // simply delete

      this.prodVendors.splice(index, 1);

    } else {
      // Cancel editing of existing record
      // have to reload it from database

      this.prodVendors[index] = { ...this.prodVendorsClone.find(sg => sg.cloneIndex == esg.cloneIndex) };
      this.prodVendors[index].cloneIndex = 0;
    }

    this.prodVendorsClone.splice(this.prodVendorsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex), 1);

    this.editMode = false;
  }

  /**
   * @description Calculate product marging on product retail price
   * @param event
   * @param pv
   */
  onOutcomePriceChange(event, pv: ProdVendors) {
    console.log('*** EditProductVendorsComponent in onOutcomePriceChange: ' + JSON.stringify(event));

    this.pvPriceUpdate$.next(pv);
    pv.VENDORMARGIN = pv.VPOUTCOMEPRICE / pv.VPINCOMEPRICE;
  }

  /**
   * @description Calculate product retail price on change margin
   * @param event
   * @param pv
   */
  onMarginChange(event, pv: ProdVendors) {
    console.log('*** EditProductVendorsComponent in onMarginChange: ' + JSON.stringify(event));
//    environment.production || console.table(pv);

    this.pvPriceUpdate$.next(pv);
    pv.VPOUTCOMEPRICE = pv.VENDORMARGIN * pv.VPINCOMEPRICE;
  }

  onPvRowEditSave(esg: ProdVendors) {
    console.log('*** EditProductVendorsComponent in onRowEditSave: ');
    environment.production || console.table(esg);

    this._productVendorsQuery = iif(() => (esg.VENDORSITEMID == -2),

      of(esg).pipe(
        filter(esg => esg.VENDORSITEMID == -2),
        concatMap(esg => this.prodApi.postProdVendorById(this.product.PRODID, esg).pipe(map(res => {
          console.log('*** EditProductVendorsComponent in onMapSave POST result: ' + JSON.stringify(res));
        }))),
        ),
      
      of(esg).pipe(
        filter(esg => esg.VENDORSITEMID != -2),
        concatMap(esg => this.prodApi.putProdVendorById(this.product.PRODID, esg.VENDORSITEMID, esg).pipe(map(res => {
          console.log('*** EditProductVendorsComponent in onMapSave PUT result: ' + JSON.stringify(res));
        })))
      ))

      .pipe(
        finalize(() => {
          environment.production || console.table(this.prodVendors);
          this.prodVendorsClone.splice(this.prodVendorsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex), 1);
          esg.cloneIndex = 0;
          this.loadProdVendors();
          this.editMode = false;
          this.productPriceUpdate.emit(this.prodVendors);
        })
      )
      .subscribe();
  }
}
